// ----------------------------------------------------------------------

import { useTheme } from "@mui/material";
import { TypographyOptions } from "@mui/material/styles/createTypography";
import React from "react";
import { responsiveFontSizes } from "src/utils/getFontValue";

const FONT_PRIMARY = 'Inter, sans-serif'; // Google Font
// const FONT_SECONDARY = 'CircularStd, sans-serif'; // Local Font
// const theme = useTheme();

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    locationNumber: true;
    header: true;
    page_title: true;
    h1_blue: true;
    h1_2: true;
    h1_2_blue: true;
    h2_blue: true;
    h3_blue: true;
    h4_blue: true;
    h5_blue: true;
    h5_2: true;
    h5_2_blue: true;
    h5_3: true;
    h5_3_blue: true;
    h6_blue: true;
    body1_blue: true;
    body1_14: true;
    body1_14_blue: true;
    body2_blue: true;
    caption_black: true;
  }
}

interface ExtendedTypographyOptions extends TypographyOptions {
  locationNumber: React.CSSProperties;
  header: React.CSSProperties;
  page_title: React.CSSProperties;
  h1_blue: React.CSSProperties;
  h1_2: React.CSSProperties;
  h1_2_blue: React.CSSProperties;
  h2_blue: React.CSSProperties;
  h3_blue: React.CSSProperties;
  h4_blue: React.CSSProperties;
  h5_blue: React.CSSProperties;
  h5_2: React.CSSProperties;
  h5_2_blue: React.CSSProperties;
  h5_3: React.CSSProperties;
  h6_blue: React.CSSProperties;
  body1_blue: React.CSSProperties;
  body1_14: React.CSSProperties;
  body1_14_blue: React.CSSProperties;
  body2_blue: React.CSSProperties;
  caption_black: React.CSSProperties;
}

const typography = {
  fontFamily: FONT_PRIMARY,
  fontWeightRegular: 400,
  fontWeightMedium: 600,
  fontWeightBold: 700,

  locationNumber: {
    fontSize: '40px',
    fontWeight: '600',
    lineHeight: '50px',
    color: '#0627B7',
  },

  header: {
    fontWeight: 700,
    lineHeight: '40px',
    fontSize: '70px',
    paddingRight: '10px',
    ...responsiveFontSizes({ sm: 40, md: 50, lg: 70 }),
  },
  page_title: {
    fontWeight: 500,
    lineHeight: '24px',
    fontSize: '20px',
    marginBottom: '10px',
    backgroundColor: '#bcc4df',
    padding: '10px',
    borderRadius: '5px',
    ...responsiveFontSizes({ sm: 16, md: 18, lg: 20 }),
  },
  h1: {
    fontWeight: 700,
    lineHeight: '26.4px',
    fontSize: '24px',
    ...responsiveFontSizes({ sm: 20, md: 22, lg: 24 }),
  },
  h1_blue: {
    fontWeight: 700,
    lineHeight: '26.4px',
    fontSize: '24px',
    color: '#0627B7',
    ...responsiveFontSizes({ sm: 20, md: 22, lg: 24 }),
  },
  h1_2: {
    fontWeight: 600,
    lineHeight: '26.4px',
    fontSize: '24px',
    ...responsiveFontSizes({ sm: 20, md: 22, lg: 24 }),
  },
  h1_2_blue: {
    fontWeight: 600,
    lineHeight: '26.4px',
    fontSize: '24px',
    color: '#0627B7',
    ...responsiveFontSizes({ sm: 20, md: 22, lg: 24 }),
  },
  h2: {
    fontWeight: 700,
    lineHeight: '24px',
    fontSize: '20px',
    ...responsiveFontSizes({ sm: 16, md: 18, lg: 20 }),
  },
  h2_blue: {
    fontWeight: 700,
    lineHeight: '24px',
    fontSize: '20px',
    color: '#0627B7',
    ...responsiveFontSizes({ sm: 16, md: 18, lg: 20 }),
  },
  h3: {
    fontWeight: 500,
    lineHeight: '24px',
    fontSize: '20px',
    ...responsiveFontSizes({ sm: 16, md: 18, lg: 20 }),
  },
  h3_blue: {
    fontWeight: 500,
    lineHeight: '24px',
    fontSize: '20px',
    color: '#0627B7',
    ...responsiveFontSizes({ sm: 16, md: 18, lg: 20 }),
  },
  h4: {
    fontWeight: 500,
    lineHeight: '21.6px',
    fontSize: '18px',
    ...responsiveFontSizes({ sm: 14, md: 16, lg: 18 }),
  },
  h4_blue: {
    fontWeight: 500,
    lineHeight: '21.6px',
    fontSize: '18px',
    color: '#0627B7',
    ...responsiveFontSizes({ sm: 14, md: 16, lg: 18 }),
  },
  h5: {
    fontWeight: 600,
    lineHeight: '21.6px',
    fontSize: '18px',
    ...responsiveFontSizes({ sm: 16, md: 17, lg: 18 }),
  },
  h5_blue: {
    fontWeight: 600,
    lineHeight: '21.6px',
    fontSize: '18px',
    color: '#0627B7',
    ...responsiveFontSizes({ sm: 16, md: 17, lg: 18 }),
  },
  h5_2: {
    fontWeight: 700,
    lineHeight: '19.2px',
    fontSize: '16px',
    ...responsiveFontSizes({ sm: 15, md: 15, lg: 16 }),
  },
  h5_2_blue: {
    fontWeight: 700,
    lineHeight: '19.2px',
    fontSize: '16px',
    color: '#0627B7',
    ...responsiveFontSizes({ sm: 15, md: 15, lg: 16 }),
  },
  h5_3: {
    fontWeight: 700,
    lineHeight: '16.8px',
    fontSize: '14px',
    ...responsiveFontSizes({ sm: 13, md: 13, lg: 14 }),
  },
  h5_3_blue: {
    fontWeight: 700,
    lineHeight: '16.8px',
    fontSize: '14px',
    color: '#0627B7',
    ...responsiveFontSizes({ sm: 13, md: 13, lg: 14 }),
  },
  h6: {
    fontWeight: 600,
    lineHeight: '16.8px',
    fontSize: '14px',
    ...responsiveFontSizes({ sm: 13, md: 13, lg: 14 }),
  },
  h6_blue: {
    fontWeight: 600,
    lineHeight: '16.8px',
    fontSize: '14px',
    color: '#0627B7',
    ...responsiveFontSizes({ sm: 13, md: 13, lg: 14 }),
  },
  body1: {
    fontWeight: 400,
    lineHeight: '22.4px',
    fontSize: '16px',
    ...responsiveFontSizes({ sm: 14, md: 15, lg: 16 }),
  },
  body1_blue: {
    fontWeight: 400,
    lineHeight: '22.4px',
    fontSize: '16px',
    color: '#0627B7',
    ...responsiveFontSizes({ sm: 14, md: 15, lg: 16 }),
  },
  body1_14: {
    fontWeight: 400,
    lineHeight: '19.6px',
    fontSize: '14px',
    ...responsiveFontSizes({ sm: 12, md: 13, lg: 14 }),
  },
  body1_14_blue: {
    fontWeight: 400,
    lineHeight: '19.6px',
    fontSize: '14px',
    color: '#0627B7',
    ...responsiveFontSizes({ sm: 12, md: 13, lg: 14 }),
  },
  body2: {
    fontWeight: 500,
    lineHeight: '16.8px',
    fontSize: '12px',
    ...responsiveFontSizes({ sm: 11, md: 11, lg: 12 }),
  },
  body2_blue: {
    fontWeight: 500,
    lineHeight: '16.8px',
    fontSize: '12px',
    color: '#0627B7',
    ...responsiveFontSizes({ sm: 11, md: 11, lg: 12 }),
  },
  caption_black: {
    color: '#000',
    fontWeight: 400,
    lineHeight: '16.8px',
    fontSize: '10px',
    ...responsiveFontSizes({ sm: 9, md: 9, lg: 10 }),
  },
  caption: {
    color: '#A3A4A2',
    fontWeight: 400,
    lineHeight: '16.8px',
    fontSize: '10px',
    ...responsiveFontSizes({ sm: 9, md: 9, lg: 10 }),
  },
  button: {
    fontWeight: 600,
    lineHeight: '24px',
    fontSize: '14px',
    textTransform: 'capitalize',
  },
} as ExtendedTypographyOptions;

export default typography;
